import { create, isPlayerSupported, PlayerEventType, PlayerState, type MediaPlayer, type Quality } from "amazon-ivs-player";
import Hls from "hls.js";
import ivsWasmBinaryUrl from "amazon-ivs-player/dist/assets/amazon-ivs-wasmworker.min.wasm?url";
import ivsWasmWorkerUrl from "amazon-ivs-player/dist/assets/amazon-ivs-wasmworker.min.js?url";

import type { Session } from "./api";

const supportedQualityHeights = new Set([480, 720, 1080]);

export function attachPlayback(
    video: HTMLVideoElement,
    session: Session | null,
    qualityControls: HTMLElement | null,
    playerControls: HTMLElement | null,
    initialSeekSeconds: number | null = null,
): () => void {
    renderQualityControls(qualityControls, null);
    renderPlayerControls(playerControls, null);
    prepareVideoElement(video, session?.status === "live");

    const playbackUrl = resolveSessionPlaybackUrl(session);
    if (!session || !playbackUrl) {
        video.removeAttribute("src");
        video.load();
        return () => undefined;
    }

    if (session.status === "live" && isIvsPlaybackUrl(playbackUrl) && isPlayerSupported) {
        const player = create({
            wasmWorker: ivsWasmWorkerUrl,
            wasmBinary: ivsWasmBinaryUrl,
            isQualitySupported: (quality) => supportedQualityHeights.has(quality.height),
        });
        player.attachHTMLVideoElement(video);

        const syncQualityControls = () => {
            renderQualityControls(qualityControls, {
                mode: "ivs",
                player,
                video,
                playbackUrl,
            });
        };
        const syncPlayerControls = () => {
            renderPlayerControls(playerControls, {
                mode: "ivs",
                player,
                video,
            });
        };

        player.addEventListener(PlayerState.READY, syncQualityControls);
        player.addEventListener(PlayerEventType.QUALITY_CHANGED, syncQualityControls);
        player.addEventListener(PlayerState.PLAYING, syncQualityControls);
        player.addEventListener(PlayerEventType.TIME_UPDATE, syncQualityControls);
        player.addEventListener(PlayerEventType.SEEK_COMPLETED, syncQualityControls);
        player.addEventListener(PlayerState.READY, syncPlayerControls);
        player.addEventListener(PlayerState.PLAYING, syncPlayerControls);
        player.addEventListener(PlayerState.ENDED, syncPlayerControls);
        video.addEventListener("play", syncPlayerControls);
        video.addEventListener("pause", syncPlayerControls);
        video.addEventListener("volumechange", syncPlayerControls);
        player.setRebufferToLive(true);
        player.load(playbackUrl);
        void attemptPlayback(video);

        return () => {
            renderQualityControls(qualityControls, null);
            renderPlayerControls(playerControls, null);
            video.removeEventListener("play", syncPlayerControls);
            video.removeEventListener("pause", syncPlayerControls);
            video.removeEventListener("volumechange", syncPlayerControls);
            player.delete();
            video.removeAttribute("src");
            video.load();
        };
    }

    const syncStandardPlayerControls = () => {
        renderPlayerControls(playerControls, {
            mode: "standard",
            video,
        });
    };

    video.addEventListener("play", syncStandardPlayerControls);
    video.addEventListener("pause", syncStandardPlayerControls);
    video.addEventListener("volumechange", syncStandardPlayerControls);
    video.addEventListener("ended", syncStandardPlayerControls);

    if (Hls.isSupported()) {
        const hls = new Hls();
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
            applyInitialSeek(video, initialSeekSeconds);
            void attemptPlayback(video);
            syncStandardPlayerControls();
        });
        hls.loadSource(playbackUrl);
        hls.attachMedia(video);

        return () => {
            renderQualityControls(qualityControls, null);
            renderPlayerControls(playerControls, null);
            video.removeEventListener("play", syncStandardPlayerControls);
            video.removeEventListener("pause", syncStandardPlayerControls);
            video.removeEventListener("volumechange", syncStandardPlayerControls);
            video.removeEventListener("ended", syncStandardPlayerControls);
            hls.destroy();
            video.removeAttribute("src");
            video.load();
        };
    }

    video.src = playbackUrl;
    applyInitialSeek(video, initialSeekSeconds);
    void attemptPlayback(video);
    syncStandardPlayerControls();
    return () => {
        renderQualityControls(qualityControls, null);
        renderPlayerControls(playerControls, null);
        video.removeEventListener("play", syncStandardPlayerControls);
        video.removeEventListener("pause", syncStandardPlayerControls);
        video.removeEventListener("volumechange", syncStandardPlayerControls);
        video.removeEventListener("ended", syncStandardPlayerControls);
        video.removeAttribute("src");
        video.load();
    };
}

function applyInitialSeek(video: HTMLVideoElement, initialSeekSeconds: number | null): void {
    if (initialSeekSeconds == null || initialSeekSeconds <= 0) {
        return;
    }

    const seekToOffset = () => {
        try {
            video.currentTime = initialSeekSeconds;
        } catch {
            // Ignore browsers that reject early currentTime updates.
        }
    };

    if (video.readyState >= 1) {
        seekToOffset();
        return;
    }

    video.addEventListener("loadedmetadata", seekToOffset, {
        once: true,
    });
}

function prepareVideoElement(video: HTMLVideoElement, isLivePlayback: boolean): void {
    video.autoplay = true;
    video.playsInline = true;
    video.defaultMuted = false;
    video.muted = isLivePlayback;
    video.defaultMuted = isLivePlayback;
    video.volume = video.volume === 0 ? 1 : video.volume;
    video.removeAttribute("muted");
    video.controls = true;
}

async function attemptPlayback(video: HTMLVideoElement): Promise<void> {
    try {
        await video.play();
    } catch {
        // Ignore autoplay failures; the custom control bar still allows manual playback.
    }
}

function renderQualityControls(
    container: HTMLElement | null,
    input: { mode: "ivs"; player: MediaPlayer; video: HTMLVideoElement; playbackUrl: string; } | null,
): void {
    if (!container) {
        return;
    }

    if (!input) {
        container.innerHTML = "";
        return;
    }

    const qualities = input.player
        .getQualities()
        .sort((left, right) => right.height - left.height);

    if (qualities.length === 0) {
        container.innerHTML = "";
        return;
    }

    const activeQuality = input.player.getQuality();
    const autoMode = input.player.isAutoQualityMode();
    const atLiveEdge = isAtLiveEdge(input.video);
    const buttons = [
        renderQualityButton("Live", atLiveEdge, "live-edge"),
        renderQualityButton("Auto", autoMode, "auto"),
        ...qualities.map((quality) => renderQualityButton(formatQualityLabel(quality), !autoMode && isSameQuality(activeQuality, quality), quality.name)),
    ];

    container.innerHTML = `
        <div class="quality-toolbar">
            <span class="quality-label">Quality</span>
            <div class="quality-buttons">${buttons.join("")}</div>
        </div>
    `;

    container.querySelectorAll<HTMLButtonElement>("[data-quality-choice]").forEach((button) => {
        button.addEventListener("click", () => {
            const choice = button.dataset.qualityChoice;
            if (!choice) {
                return;
            }

            if (choice === "live-edge") {
                jumpToLiveEdge(input.player, input.playbackUrl);
                renderQualityControls(container, input);
                return;
            }

            if (choice === "auto") {
                input.player.setAutoQualityMode(true);
                renderQualityControls(container, input);
                return;
            }

            const selectedQuality = qualities.find((quality) => quality.name === choice);
            if (!selectedQuality) {
                return;
            }

            input.player.setAutoQualityMode(false);
            input.player.setQuality(selectedQuality, false);
            renderQualityControls(container, input);
        });
    });
}

function renderPlayerControls(
    container: HTMLElement | null,
    input:
        | { mode: "ivs"; player: MediaPlayer; video: HTMLVideoElement; }
        | { mode: "standard"; video: HTMLVideoElement; }
        | null,
): void {
    if (!container) {
        return;
    }

    if (!input) {
        container.innerHTML = "";
        return;
    }

    const isPlaying = !input.video.paused && !input.video.ended;
    const isMuted = input.video.muted || input.video.volume === 0;

    container.innerHTML = `
        <div class="quality-toolbar player-toolbar">
            <span class="quality-label">Player</span>
            <div class="quality-buttons">
                <button type="button" class="quality-button" data-player-action="toggle-play">${isPlaying ? "Pause" : "Play"}</button>
                <button type="button" class="quality-button" data-player-action="toggle-mute">${isMuted ? "Unmute" : "Mute"}</button>
                <button type="button" class="quality-button" data-player-action="fullscreen">Fullscreen</button>
            </div>
        </div>
    `;

    container.querySelectorAll<HTMLButtonElement>("[data-player-action]").forEach((button) => {
        button.addEventListener("click", () => {
            const action = button.dataset.playerAction;
            if (!action) {
                return;
            }

            if (action === "toggle-play") {
                if (input.video.paused || input.video.ended) {
                    void input.video.play();
                } else {
                    input.video.pause();
                }
                return;
            }

            if (action === "toggle-mute") {
                input.video.muted = !isMuted;
                if (!input.video.muted && input.video.volume === 0) {
                    input.video.volume = 1;
                }
                renderPlayerControls(container, input);
                return;
            }

            if (action === "fullscreen") {
                void input.video.requestFullscreen?.();
            }
        });
    });
}

function renderQualityButton(label: string, selected: boolean, choice: string): string {
    return `
        <button
            type="button"
            class="quality-button ${selected ? "selected" : ""}"
            data-quality-choice="${escapeHtmlAttribute(choice)}"
        >
            ${escapeHtml(label)}
        </button>
    `;
}

function formatQualityLabel(quality: Quality): string {
    return quality.height > 0 ? `${quality.height}p` : quality.name;
}

function isSameQuality(left: Quality, right: Quality): boolean {
    return left.name === right.name && left.height === right.height && left.bitrate === right.bitrate;
}

function jumpToLiveEdge(player: MediaPlayer, playbackUrl: string): void {
    player.setRebufferToLive(true);
    player.load(playbackUrl);
    player.play();
}

function isAtLiveEdge(video: HTMLVideoElement): boolean {
    const seekable = video.seekable;
    if (seekable.length === 0) {
        return true;
    }

    const liveEdge = seekable.end(seekable.length - 1);
    return liveEdge - video.currentTime <= 2;
}

function isIvsPlaybackUrl(playbackUrl: string): boolean {
    return playbackUrl.includes(".playback.live-video.net/");
}

function resolveSessionPlaybackUrl(session: Session | null): string | null {
    if (!session) {
        return null;
    }

    if (session.status === "live" && session.playbackUrl) {
        return session.playbackUrl;
    }

    if (session.recordingStatus === "available" && session.recordingPlaybackUrl) {
        return session.recordingPlaybackUrl;
    }

    return null;
}

function escapeHtml(value: string): string {
    return value
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;")
        .replaceAll("\"", "&quot;")
        .replaceAll("'", "&#39;");
}

function escapeHtmlAttribute(value: string): string {
    return escapeHtml(value);
}
